import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, Building2, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/config";
import { SEO } from "@/components/SEO";
import PhoneInputFr from "@/components/PhoneInputFr";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const brandIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [map, positions]);
  return null;
}

export default function ContactPage() {
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const res = await axios.get(`${API}/agencies`);
        setAgencies(res.data);
      } catch { /* ignore */ }
    };
    fetchAgencies();
  }, []);

  const agenciesWithCoords = agencies.filter((a) => a.latitude && a.longitude);
  const mapCenter = [46.6, 2.5]; // Center of France

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact - Nos agences en France"
        description="Contactez FR INTERIM. Retrouvez nos agences sur la carte et contactez-les directement. Agence de travail temporaire spécialisée logistique."
        path="/contact"
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1758520144426-edf40a58f299?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxqb2IlMjBpbnRlcnZpZXclMjBvZmZpY2V8ZW58MHx8fHwxNzc2MTczNTkwfDA&ixlib=rb-4.1.0&q=85')" }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4">Contact</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">Contactez-nous</motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-200 leading-relaxed">
              Sélectionnez une agence pour la contacter directement.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Map + Agencies */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Nos agences</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F]">
              Trouvez l'agence la plus proche
            </motion.h2>
          </motion.div>

          {agencies.length > 0 ? (
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Map */}
              <div className="lg:col-span-3 h-[500px] border border-gray-200 z-0" data-testid="agencies-map">
                <MapContainer
                  center={mapCenter}
                  zoom={agenciesWithCoords.length > 0 ? 6 : 6}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds positions={agenciesWithCoords.map((a) => [a.latitude, a.longitude])} />
                  {agenciesWithCoords.map((agency) => (
                    <Marker
                      key={agency.agency_id}
                      position={[agency.latitude, agency.longitude]}
                      icon={brandIcon}
                      eventHandlers={{
                        click: () => setSelectedAgency(agency),
                      }}
                    >
                      <Popup>
                        <div className="text-sm">
                          <strong>{agency.name}</strong><br />
                          {agency.address}, {agency.postal_code} {agency.city}<br />
                          {agency.phone && <>{agency.phone}<br /></>}
                          <button
                            onClick={() => setSelectedAgency(agency)}
                            className="text-[#662483] font-semibold mt-1 underline"
                          >
                            Contacter cette agence
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>

              {/* Agency List */}
              <div className="lg:col-span-2 space-y-3 max-h-[500px] overflow-y-auto">
                {agencies.map((agency, i) => (
                  <motion.button
                    key={agency.agency_id}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                    variants={fadeUp}
                    onClick={() => setSelectedAgency(agency)}
                    className={`w-full text-left p-4 border transition-all card-hover ${
                      selectedAgency?.agency_id === agency.agency_id
                        ? "border-[#662483] bg-[#F0E9F3]"
                        : "border-gray-200 bg-white hover:border-[#662483]/40"
                    }`}
                    data-testid={`agency-card-${agency.agency_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[#3F3F3F] mb-1">{agency.name}</h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" /> {agency.address}, {agency.postal_code} {agency.city}
                        </p>
                        {agency.phone && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3 shrink-0" /> {agency.phone}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Les agences seront bientôt disponibles.</p>
            </div>
          )}
        </div>
      </section>

      {/* Agency Contact Dialog */}
      {selectedAgency && (
        <AgencyContactForm agency={selectedAgency} onClose={() => setSelectedAgency(null)} />
      )}
    </main>
  );
}

function AgencyContactForm({ agency, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        agency_id: agency.agency_id,
        agency_name: agency.name,
      });
      toast.success(`Message envoyé à ${agency.name} avec succès !`);
      onClose();
    } catch {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="agency-contact-modal">
      <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="brand-gradient p-5 text-white flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#E5CD96] mb-1">Contacter l'agence</p>
            <h3 className="text-xl font-bold">{agency.name}</h3>
            <div className="mt-2 space-y-1 text-sm text-white/80">
              <p className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {agency.address}, {agency.postal_code} {agency.city}</p>
              {agency.phone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> <a href={`tel:${agency.phone}`} className="underline">{agency.phone}</a></p>}
              {agency.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> <a href={`mailto:${agency.email}`} className="underline">{agency.email}</a></p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-contact-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="agency-contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-gray-300" placeholder="Votre nom" data-testid="agency-contact-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-gray-300" placeholder="votre@email.com" data-testid="agency-contact-email" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Téléphone</Label>
            <PhoneInputFr value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} data-testid="agency-contact-phone" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Message *</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none border-gray-300 min-h-[100px]" placeholder="Votre message..." data-testid="agency-contact-message" />
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={sending} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-6 py-2 rounded-none hover:opacity-90" data-testid="agency-contact-submit">
              {sending ? "Envoi..." : "Envoyer"} <Send className="ml-2 w-4 h-4" />
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
